import React, { useEffect, useMemo, useReducer, useState } from 'react';
import BsModal from '../Common/BsModal';
import { useAccounts } from '../../DataStore/AccountsContext';
import TextInput from '../Common/TextInput';
import CallOut from '../Common/CallOut';
import Notification from '../Common/Notification';
import VendorSelector from '../Transaction/VendorSelector';
import { useTag } from '../../DataStore/TagContext';
import ajaxPeriod from '../../util/remote/ajaxPeriod';

const INITIAL_FORM = {
  loading: false,
  accountType: 'Asset',
  openingAccountId: '',
  amount: '',
  payee: '',
  description: '',
  reference: '',
  tag: '0',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET':
      return { ...state, [action.key]: action.value };

    case 'PATCH':
      return { ...state, ...action.payload };

    case 'RESET':
      return { ...INITIAL_FORM };

    // helpful when switching account type
    case 'SET_TYPE':
      return {
        ...state,
        accountType: action.value,
        openingAccountId: '',
        payee: '',
      };

    // for async save
    case 'SAVING':
      return { ...state, loading: true, info: null };

    case 'SAVED_OK':
      return { ...state, loading: false, info: action.info };

    case 'SAVED_FAIL':
      return { ...state, loading: false, info: action.info };

    default:
      return state;
  }
};

const NewOpeningBalanceModal = ({ show, onClose, onSuccess }) => {
  const [info, setInfo] = useState(null);
  const {
    getAllAccounts,
    getPrimitiveAccountsByType,
    accountsCatalogue: chartOfAccounts, // alias
  } = useAccounts();

  const { getTagListing, tagListing } = useTag();

  const [form, dispatch] = useReducer(reducer, INITIAL_FORM);

  useEffect(() => {
    getAllAccounts();
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reset local state when modal closes
  useEffect(() => {
    if (!show) dispatch({ type: 'RESET' });
  }, [show]);

  const payeeRequired = useMemo(() => form.accountType === 'Liability', [form.accountType]);

  const primitiveOptions = useMemo(() => {
    if (!Array.isArray(chartOfAccounts)) return [];
    const list = typeof getPrimitiveAccountsByType === 'function'
      ? getPrimitiveAccountsByType(form.accountType)
      : [];
    return list.map((a) => ({
      label: `${a.account_number} ${a.account_name}`,
      value: a.account_id,
    }));
  }, [form.accountType, chartOfAccounts, getPrimitiveAccountsByType]);

  const tagOptions = useMemo(() => {
    const opts = [{ label: '- No project -', value: '0' }];
    if (Array.isArray(tagListing)) {
      tagListing.forEach((t) => opts.push({ label: t.tag_name, value: t.tag_id }));
    }
    return opts;
  }, [tagListing]);

  const validate = () => {
    const n = Number(String(form.amount).replace(/,/g, ''));

    if (!form.openingAccountId) return 'Please select the opening balance account.';
    if (!form.amount || Number.isNaN(n) || n <= 0) return 'Please enter a valid opening balance amount.';
    if (payeeRequired && !String(form.payee || '').trim()) return 'Please enter a payee for this opening liability.';
    if (String(form.reference || '').trim().length === 0) return 'Please enter a reference for this opening balance.';

    return null;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    const err = validate();
    if (err) {
      setInfo(<Notification message={err} type="danger" />);
      return;
    }

    const payload = {
      account_id: form.openingAccountId,
      amount: Number(String(form.amount).replace(/,/g, '')),
      payee_id: String(form.payee || '').trim() || '0',
      description: String(form.description || '').trim() || 'OPENING BALANCE',
      reference: String(form.reference || '').trim() || '',
      tag: form.tag || '0',
    };

    dispatch({ type: 'SAVING' });
    const server_response = await ajaxPeriod.postOpeningBalance(payload);

    if (server_response?.status === 'OK') {
      dispatch({ type: 'RESET' });
      setInfo(<Notification message={server_response.message || 'Opening balance saved.'} type="success" />)
      onSuccess?.();
    } else {
      setInfo(<Notification message={server_response?.message || 'Failed to save opening balance.'} type="danger" />);  
    }
  };

  const RenderOpeningAccount = () => (
    <TextInput
      type="select"
      label={`${form.accountType} Account`}
      placeholder={`Select ${form.accountType ? form.accountType.toLowerCase() : ''} account`}
      leftIcon="fa fa-folder-open"
      options={primitiveOptions}
      onChange={(item) => dispatch({ type: 'SET', key: 'openingAccountId', value: item?.value })}
      loading={!Array.isArray(chartOfAccounts)}
      required
      wrap
      value={form.openingAccountId}
      leftComponent={
        <i
          className="fa fa-folder-open"
          title="Refresh chart of accounts"
          onClick={() => getAllAccounts(true)}
        />
      }
    />
  );

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="New Opening Balance"
      size="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-modern-secondary" onClick={onClose} disabled={form.loading}>
            Close
          </button>

          <button type="button" className="btn btn-modern-primary" onClick={handleSave} disabled={form.loading}>
            {form.loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle mr-2" /> Save Opening Balance
              </>
            )}
          </button>
        </>
      }
    >
      <div style={{ padding: 8 }}>
        <CallOut
          title="Tip"
          description="Record what your business started with at the beginning of the financial year (Assets, Liabilities, or Equity)."
        />

        <div className="mt-3">{info}</div>

        <form className="form-horizontal" method="post" onSubmit={handleSave}>
          <div className="row">
            <div className="col-md-6">
              <TextInput
                type="select"
                label="Account Type"
                placeholder="Select type"
                leftIcon="fa fa-sitemap"
                options={[
                  { label: 'Asset', value: 'Asset' },
                  { label: 'Liability', value: 'Liability' },
                  { label: 'Equity', value: 'Equity' },
                ]}
                value={form.accountType || 'Asset'}
                onChange={(item) => dispatch({ type: 'SET_TYPE', value: item?.value })}
                required
                wrap
              />
            </div>

            <div className="col-md-6">{RenderOpeningAccount()}</div>

            <div className="col-md-6">
              <TextInput
                type="number"
                label="Amount"
                placeholder="0.00"
                leftIcon="fa fa-credit-card"
                value={form.amount}
                onChange={(e) => dispatch({ type: 'SET', key: 'amount', value: e.target.value })}
                required
                wrap
              />
            </div>

            <div className="col-md-6">
              <VendorSelector
                onChange={(item) => dispatch({ type: 'SET', key: 'payee', value: item?.value })}
                label={payeeRequired ? 'Payee (Required)' : 'Payee (Optional)'}
                placeholder={payeeRequired ? 'e.g. Supplier' : 'e.g. Supplier / Customer'}
                value={form.payee}
                wrap
                showCreate={false}
              />
              {payeeRequired && (
                <small className="text-muted d-block" style={{ marginTop: -6 }}>
                  Tip: For unpaid invoices / payables, include who the balance is owed to.
                </small>
              )}
            </div>

            <div className="col-md-6">
              <TextInput
                type="text"
                label="Reference"
                placeholder="Reference/Note"
                leftIcon="fa fa-credit-card"
                value={form.reference}
                onChange={(e) => dispatch({ type: 'SET', key: 'reference', value: e.target.value })}
                required
                wrap
              />
            </div>

            <div className="col-md-6">
              <TextInput
                type="select"
                label="Project (Optional)"
                placeholder="Select project (if applicable)"
                leftIcon="fa fa-tag"
                options={tagOptions}
                onChange={(item) => dispatch({ type: 'SET', key: 'tag', value: item?.value })}
                leftComponent={<i className="fa fa-tag link" onClick={() => getTagListing(true)} />}
                loading={!tagListing}
                value={form.tag}
                wrap
              />
            </div>

            <div className="col-12">
              <TextInput
                type="textarea"
                label="Description"
                placeholder="Short note about this opening balance..."
                leftIcon="fa fa-align-left"
                value={form.description}
                onChange={(e) => dispatch({ type: 'SET', key: 'description', value: e.target.value })}
                wrap
              />
            </div>

            {Array.isArray(chartOfAccounts) && primitiveOptions.length === 0 && form.accountType && (
              <div className="col-12">
                <div className="alert alert-warning mt-2 mb-0">
                  No primitive {form.accountType.toLowerCase()} accounts found. Add sub-accounts under that category first.
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </BsModal>
  );
};

export default NewOpeningBalanceModal;
